// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadCurrentBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadCurrentBillTo";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CutEdgeContainer from "@stanion/App/Components/CutEdge/CutEdge";
import { XNGAGE_CUSTOM_WIDGET } from "@stanion/XDL/constants";
import Email from "@stanion/XDL/Icons/Email";
import OpenInvoices from "@stanion/XDL/Icons/OpenInvoices";
import OpenOrders from "@stanion/XDL/Icons/OpenOrders";
import OpenQuotes from "@stanion/XDL/Icons/OpenQuotes";
import Phone from "@stanion/XDL/Icons/Phone";
import ProductLists from "@stanion/XDL/Icons/ProductLists";
import { color, xScreen } from "@stanion/XDL/Utilities";
import React, { FC, useEffect } from "react";
import { useDispatch } from "react-redux";
import { css } from "styled-components";

const enum fields {
    itemsList = "itemsList",
}

interface OwnProps {
    fields: {
        [fields.itemsList]: {
            fields: {
                title: string;
                linkTitle: string;
                link: LinkFieldValue;
                type: "openQuotes" | "openInvoices" | "openOrders" | "productLists";
            };
        }[];
    };
}

type Props = OwnProps & WidgetProps;

interface AccountTilesStyles {
    wrapper?: InjectableCss;
    infoSection?: InjectableCss;
    titleSection?: InjectableCss;
    title?: TypographyPresentationProps;
    subtitle?: TypographyPresentationProps;
    customerInfoWrapper?: InjectableCss;
    customerInfoTitle?: InjectableCss;
    customerInfo?: InjectableCss;
    customerLinks?: InjectableCss;
    blockContent?: InjectableCss;
    item?: {
        title?: TypographyPresentationProps;
        icon?: IconPresentationProps;
        link?: LinkPresentationProps;
        number?: TypographyPresentationProps;
    };
    link?: LinkPresentationProps;
    titleWrapper?: InjectableCss;
    blocksGridContainer?: GridContainerProps;
    blockGridItem?: GridItemProps;
    // XNG-Chnages:
    managerLink?: LinkPresentationProps;
    phoneLink?: LinkPresentationProps;
}

const AccountTilesStyles: AccountTilesStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            flex-wrap: wrap;
            padding: 16px;
            gap: 30px 60px;
        `,
    },
    infoSection: {
        css: css`
            display: flex;
            align-items: flex-start;
            justify-content: flex-start;
            flex-direction: column;
            min-width: 268px;
            gap: 16px;

            ${xScreen("lte", "sm")} {
                flex-direction: column;
                align-items: flex-start;
                justify-content: flex-start;
                width: 100%;
            }
        `,
    },
    titleSection: {
        css: css`
            display: flex;
            align-items: center;
            ${xScreen("lte", "xs")} {
                flex-direction: column;
                align-items: flex-start;
            }
        `,
    },
    title: {
        variant: "h1",
        size: 38,
        css: css`
            margin-bottom: 0;
            font-weight: 600;
        `,
    },
    subtitle: {
        variant: "h1",
        size: 38,
        css: css`
            margin-bottom: 0;
            font-weight: 800;
            color: ${color("primary.main")};
            &::before {
                content: "\00a0";
            }
            ${xScreen("lte", "xs")} {
                margin: 0px;
                &::before {
                    content: none;
                }
            }
        `,
    },
    titleWrapper: {
        css: css`
            display: flex;
            align-items: baseline;
            column-gap: 8px;
        `,
    },
    customerInfoWrapper: {
        css: css`
            display: flex;
            ${xScreen("lte", "md")} {
                flex-direction: column;
                row-gap: 16px;
            }
        `,
    },
    customerInfoTitle: {
        css: css`
            font-weight: bold;
            ${xScreen("lte", "sm")} {
                row-gap: 10px;
            }
        `,
    },
    customerInfo: {
        css: css`
            display: flex;
            column-gap: 10px;
            margin-right: 32px;
            color: ${color("common.ui6")};
        `,
    },
    customerLinks: {
        css: css`
            display: flex;
            column-gap: 21px;
            margin-right: 32px;
            color: ${color("common.ui6")};

            ${xScreen("lte", "xs")} {
                flex-direction: column;
                align-items: flex-start;
                justify-content: flex-start;
                width: 100%;
                row-gap: 10px;
            }
        `,
    },
    item: {
        title: {
            variant: "h5",
            size: 18,
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 22px;
                margin-bottom: 0;
            `,
        },
        link: {
            typographyProps: {
                size: 14,
                weight: "bold",
            },
        },
        icon: {
            size: 26,
            css: css`
                flex-shrink: 0;
                margin-right: 23px;
            `,
        },
        number: {
            weight: 800,
            size: 26,
            color: "primary.main",
            variant: "h4",
            css: css`
                margin-bottom: 13px;
            `,
        },
    },
    link: {
        typographyProps: {
            weight: "bold",
            size: 16,
        },
        icon: {
            iconProps: {
                css: css`
                    margin-right: 12px;
                `,
            },
        },
    },
    blockContent: {
        css: css`
            display: flex;
        `,
    },
    blocksGridContainer: {
        gap: 22,
    },
    blockGridItem: {
        width: [12, 6, 6, 3, 3],
    },
    managerLink: {
        typographyProps: {
            weight: "bold",
            size: 16,
        },
        icon: {
            iconProps: {
                src: Email,
                color: "common.ui8",
            },
        },
    },
    phoneLink: {
        typographyProps: {
            weight: "bold",
            size: 16,
        },
        icon: {
            iconProps: {
                src: Phone,
                color: "common.ui8",
                css: css`
                    transform: rotate(-90deg);
                `,
            },
        },
    },
};

const styles = AccountTilesStyles;

const AccountTiles: FC<Props> = ({ fields }) => {
    const { firstName, availableList, customerNumber, myBranch, accountManager } = useAppSelector(state => {
        const session = getSession(state);
        return {
            firstName: session.firstName,
            availableList: {
                openQuotes: session.properties?.numOfOpenQuotes,
                openInvoices: session.properties?.numOfOpenInvoices,
                openOrders: session.properties?.numOfOpenOrders,
                productLists: session.properties?.numOfWishLists,
            },
            customerNumber: getCurrentBillToState(state).value?.customerNumber,
            myBranch: JSON.parse(session.properties["myBranch"] || "{}"),
            accountManager: JSON.parse(session.properties["accountManager"] || "{}"),
        };
    });

    const dispatch = useDispatch();

    useEffect(() => {
        if (!customerNumber) {
            dispatch(loadCurrentBillTo());
        }
    }, []);

    const icons = [
        {
            name: "openQuotes",
            value: OpenQuotes,
        },
        {
            name: "openInvoices",
            value: OpenInvoices,
        },
        {
            name: "openOrders",
            value: OpenOrders,
        },
        {
            name: "productLists",
            value: ProductLists,
        },
    ];

    const itemsLinks = useGetLinks(fields.itemsList, o => o.fields.link);

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="AccountTiles">
            <StyledWrapper {...styles.infoSection}>
                <StyledWrapper {...styles.titleSection}>
                    <Typography {...styles.title} data-test-selector="AccountTiles_title">
                        {siteMessage("Stanion_AccountTiles_Greeting")}
                        {firstName && ","}
                    </Typography>
                    {firstName && (
                        <Typography {...styles.subtitle} data-test-selector="AccountTiles_subtitle">
                            {firstName}!
                        </Typography>
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.customerInfoWrapper}>
                    <StyledWrapper {...styles.customerInfo}>
                        <Typography {...styles.customerInfoTitle}>{translate("Customer #:")}</Typography>
                        <Typography>{customerNumber}</Typography>
                    </StyledWrapper>
                    {(accountManager.Name || myBranch.Phone) && (
                        <StyledWrapper {...styles.customerLinks}>
                            <Typography {...styles.customerInfoTitle}>{translate("Account Manager:")}</Typography>
                            {accountManager.Name && (
                                <Link {...styles.managerLink} href={`mailto:${accountManager.Email}`}>
                                    {accountManager.Name}
                                </Link>
                            )}
                            {myBranch.Phone && (
                                <Link {...styles.phoneLink} href={`tel:${myBranch.Phone}`}>
                                    {myBranch.Phone}
                                </Link>
                            )}
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
            <GridContainer {...styles.blocksGridContainer}>
                {fields.itemsList?.map((itemField, index) => {
                    const item = {
                        title: itemField.fields.title,
                        numOfAvailable: availableList[itemField.fields.type],
                        linkTitle: itemField.fields.linkTitle,
                        link: itemsLinks?.[index].url || "",
                        icon: icons.find(icon => icon.name === itemField.fields.type)?.value,
                    };
                    return (
                        <GridItem {...styles.blockGridItem} key={item.title}>
                            <CutEdgeContainer
                                edgeSize="16px"
                                borderColor="common.border"
                                backgroundColor="common.white"
                                customWidth="100%"
                                padding="20px"
                            >
                                <StyledWrapper {...styles.blockContent}>
                                    <Icon src={item.icon} {...styles.item?.icon} />
                                    <StyledWrapper>
                                        <StyledWrapper {...styles.titleWrapper}>
                                            <Typography {...styles.item?.number}>{item.numOfAvailable}</Typography>
                                            <Typography {...styles.item?.title}>{item.title}</Typography>
                                        </StyledWrapper>
                                        <Link href={item.link} {...styles.item?.link}>
                                            {item.linkTitle}
                                        </Link>
                                    </StyledWrapper>
                                </StyledWrapper>
                            </CutEdgeContainer>
                        </GridItem>
                    );
                })}
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: AccountTiles,
    definition: {
        group: XNGAGE_CUSTOM_WIDGET,
        fieldDefinitions: [
            {
                name: "itemsList",
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "General",
                isVisible: item => !item.fields.isAuthenticated,
                fieldDefinitions: [
                    {
                        name: "type",
                        displayName: "Type",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Open Quotes", value: "openQuotes" },
                            { displayName: "Open Orders", value: "openOrders" },
                            { displayName: "Open Invoices", value: "openInvoices" },
                            { displayName: "Product Lists", value: "productLists" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "openQuotes",
                    },
                    {
                        name: "title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "linkTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Url", value: "" },
                        allowCategories: item => false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
